import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout, adminToolPath } from '../../components/AdminLayout';
import { TaskShell } from '../../components/TaskShell';
import { useViewModelReady } from '../../hooks/useViewModel';
import { InstructionsEditor } from './InstructionsEditor';
import { useAdminTaskTypeViewModel } from './viewModel';
import { paths } from '../../routes/paths';

export function AdminTaskTypePreviewPage() {
  const { isAdmin } = useAuth();
  const { taskTypeId } = useParams<{ taskTypeId: string }>();
  const navigate = useNavigate();

  const vm = useAdminTaskTypeViewModel(taskTypeId);
  useViewModelReady(vm.initialize);
  const { descriptor, runtime, instructionsState } = vm;
  const module = runtime.module;

  const [result, setResult] = useState<unknown>(module?.sampleResult ?? null);
  useEffect(() => {
    setResult(module?.sampleResult ?? null);
  }, [module]);

  let preview: React.ReactNode;
  if (runtime.status === 'loading') {
    preview = (
      <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-12 text-sm text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading task runtime…
      </div>
    );
  } else if (runtime.status === 'error' || !module) {
    preview = (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <p className="font-medium">The task runtime could not be loaded.</p>
        {runtime.error && <p className="mt-1 text-xs text-red-700">{runtime.error.message}</p>}
        <button
          type="button"
          onClick={runtime.retry}
          className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          Try again
        </button>
      </div>
    );
  } else {
    // `embedded` keeps the shell in flow layout so it stays inside the column.
    preview = (
      <TaskShell
        mode="preview"
        module={module}
        result={result}
        onResultChange={setResult}
        instructions={instructionsState.instructions}
        embedded={true}
      />
    );
  }

  if (!isAdmin) {
    return <Navigate to={paths.queue()} replace />;
  }
  if (!taskTypeId) {
    return <Navigate to={adminToolPath('taskTypes')} replace />;
  }

  return (
    <AdminLayout
      activeId="taskTypes"
      onSelectTool={(id) => navigate(adminToolPath(id))}
      backLabel="Back to admin"
      onBack={() => navigate(adminToolPath('taskTypes'))}
    >
      {({ mobileNavTrigger }) => (
        <section className="min-w-0 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={adminToolPath('taskTypes')}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to task types
              </Link>
              <h1 className="mt-1 text-xl font-bold text-gray-900 sm:text-2xl">
                {descriptor ? descriptor.displayName : 'Task type'}
              </h1>
              {descriptor?.description && (
                <p className="mt-1 max-w-prose text-sm text-gray-500">{descriptor.description}</p>
              )}
            </div>
            {mobileNavTrigger}
          </div>

          {!descriptor ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
              Task type <span className="font-mono">{taskTypeId}</span> isn't registered.
              <div className="mt-3">
                <Link
                  to={adminToolPath('taskTypes')}
                  className="text-sm font-medium text-primary hover:text-primary-hover"
                >
                  ← Back to task types
                </Link>
              </div>
            </div>
          ) : vm.busy ? null : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="min-w-0 space-y-4 lg:col-span-1">
                <InstructionsEditor
                  taskTypeId={descriptor.id}
                  current={instructionsState.instructions}
                  loading={instructionsState.loading}
                  saving={vm.saving}
                  onSave={vm.save}
                />
              </div>
              <div className="min-w-0 lg:col-span-2">{preview}</div>
            </div>
          )}
        </section>
      )}
    </AdminLayout>
  );
}
